import React from 'react';
import styled from 'styled-components';
import { Card, Tag, Button, Empty, Popconfirm, Pagination, Tooltip } from 'antd';
import {
  PiFileZipBold,
  PiTrashBold,
  PiPencilSimpleBold,
  PiDatabaseBold,
  PiCalendarBlankBold,
} from 'react-icons/pi';
import { CollectFormTemplate } from '~/common/typescript/models/CollectFormTemplate';
import { formatDate } from '~/utils/dateUtils';

interface CollectFormTemplateGridProps {
  forms: CollectFormTemplate[];
  onEdit: (form: CollectFormTemplate) => void;
  onDelete: (uuid: string) => void;
  onExportZip: (form: CollectFormTemplate) => void;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number, pageSize: number) => void;
}

export const CollectFormTemplateGrid: React.FC<CollectFormTemplateGridProps> = ({
  forms,
  onEdit,
  onDelete,
  onExportZip,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
}) => {
  if (forms.length === 0) {
    return (
      <UI_EmptyWrapper>
        <Empty description="Không có mẫu biểu mẫu nào phù hợp" />
      </UI_EmptyWrapper>
    );
  }

  return (
    <UI_Container>
      <UI_Grid>
        {forms.map((form) => {
          const fieldCount = form.content?.fields?.length || 0;
          const sectionCount = form.content?.sections?.length || 0;
          const hasSqlite = Boolean(form.sqlite_files);

          return (
            <UI_Card key={form.uuid} hoverable>
              <UI_CardHeader>
                <UI_CardTitleRow>
                  <UI_CardTitle title={form.name}>{form.name}</UI_CardTitle>
                </UI_CardTitleRow>
                <UI_CardDescription title={form.description || ''}>
                  {form.description || 'Không có mô tả'}
                </UI_CardDescription>
              </UI_CardHeader>

              <UI_CardMeta>
                <UI_TagGroup>
                  <Tag color="blue">{fieldCount} trường</Tag>
                  {sectionCount > 0 && <Tag color="cyan">{sectionCount} phần</Tag>}
                  {hasSqlite && (
                    <Tag icon={<PiDatabaseBold style={{ verticalAlign: -1 }} />} color="purple">
                      SQLite
                    </Tag>
                  )}
                </UI_TagGroup>

                <UI_DateText>
                  <PiCalendarBlankBold size={13} />
                  <span>{formatDate(form.created_at)}</span>
                </UI_DateText>
              </UI_CardMeta>

              <UI_CardActions>
                <Button
                  size="small"
                  type="primary"
                  ghost
                  icon={<PiPencilSimpleBold size={14} />}
                  onClick={() => onEdit(form)}
                  style={{ borderRadius: 4 }}
                >
                  Thiết kế
                </Button>

                <Tooltip title="Xuất gói ZIP">
                  <Button
                    size="small"
                    icon={<PiFileZipBold size={14} />}
                    onClick={() => onExportZip(form)}
                    style={{ borderRadius: 4 }}
                  >
                    Xuất ZIP
                  </Button>
                </Tooltip>

                <Popconfirm
                  title="Xoá biểu mẫu"
                  description="Bạn có chắc chắn muốn xoá mẫu biểu mẫu này không?"
                  onConfirm={() => onDelete(form.uuid)}
                  okText="Xoá"
                  cancelText="Huỷ"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<PiTrashBold size={14} />}
                    style={{ borderRadius: 4 }}
                  />
                </Popconfirm>
              </UI_CardActions>
            </UI_Card>
          );
        })}
      </UI_Grid>

      {totalCount > 0 && (
        <UI_PaginationWrapper>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalCount}
            pageSizeOptions={[6, 12, 24, 48]}
            showSizeChanger
            showTotal={(total, range) => `${range[0]}-${range[1]} của ${total} template`}
            onChange={onPageChange}
          />
        </UI_PaginationWrapper>
      )}
    </UI_Container>
  );
};

const UI_Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 20px;
`;

const UI_Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const UI_Card = styled(Card)`
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
  }

  .ant-card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    gap: 12px;
  }
`;

const UI_CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UI_CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UI_CardTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UI_CardDescription = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
  height: 36px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const UI_CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UI_TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const UI_DateText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #94a3b8;
`;

const UI_CardActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
`;

const UI_PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 12px;
`;

const UI_EmptyWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
`;
